import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { getQuizQuestions } from 'graphql/queries';
import differenceBy from 'lodash/differenceBy';
import {
    updateQuizQuestionAndAnswer,
    deleteQuizQuestionsById,
    bulkInsertQuestions,
    updateQuestionsMultipleAnswers
} from 'graphql/mutations';
import {
    QuizQuestions,
    QuizQuestionsVariables,
    QuizQuestions_quiz_master_quizzes_by_pk_questions
} from 'graphql/queries/types/QuizQuestions';
import { BulkUploadJson, ConfirmDialog, EnhancedTable, QuestionDetail, Spinner, TableHeadCell } from 'components';
import { Question, TableQuestionWithAnswer, QuestionImportModel } from 'types';
import { DeleteQuestionsById, DeleteQuestionsByIdVariables } from 'graphql/mutations/types/DeleteQuestionsById';
import { UpdateQuestionAndAnswer, UpdateQuestionAndAnswerVariables } from 'graphql/mutations/types/UpdateQuestionAndAnswer';
import { Accordion, AccordionDetails, AccordionSummary, Box, Paper, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DataObjectIcon from '@mui/icons-material/DataObject';
import {
    BulkInsertQuestionsWithAnswers,
    BulkInsertQuestionsWithAnswersVariables
} from 'graphql/mutations/types/BulkInsertQuestionsWithAnswers';

import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { useGlobalContext, useQuizContext } from 'contexts';
import { QuestionType } from 'consts';
import { quiz_master_quiz_questions_insert_input } from 'graphql/types/graphql-global-types';
import {
    UpdateQuestionMultipleAnswers,
    UpdateQuestionMultipleAnswersVariables
} from 'graphql/mutations/types/UpdateQuestionMultipleAnswers';
import { capitalizeFirstLetter, isNumber } from 'utils';

export const EditQuizQuestions = () => {
    const { quiz } = useQuizContext();
    const globalContext = useGlobalContext();
    const [editedQuestion, setQuestionToEdit] = useState<Question | null>();

    const {
        loading,
        error,
        data: quizQuestions
    } = useQuery<QuizQuestions, QuizQuestionsVariables>(getQuizQuestions, {
        variables: { quizId: quiz.id! }
    });

    const [
        deleteQuestions,
        {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            data: delteQuestionResult,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            loading: executingDelete,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            error: deleteError
        }
    ] = useMutation<DeleteQuestionsById, DeleteQuestionsByIdVariables>(deleteQuizQuestionsById, {
        refetchQueries: [getQuizQuestions]
    });

    const [
        bulkInsertQuestion,
        {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            data: bulkInsertQuestionResult,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            loading: executingBulkInsert,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            error: bulkInsertError
        }
    ] = useMutation<BulkInsertQuestionsWithAnswers, BulkInsertQuestionsWithAnswersVariables>(bulkInsertQuestions, {
        refetchQueries: [getQuizQuestions]
    });

    const [
        updateQuestion,
        {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            data: updateQuestionResult,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            loading: executingUpdate,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            error: updateError
        }
    ] = useMutation<UpdateQuestionAndAnswer, UpdateQuestionAndAnswerVariables>(updateQuizQuestionAndAnswer, {
        refetchQueries: [getQuizQuestions]
    });

    const [
        updateAllAnswers,
        {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            data: updateQuestionsMultipleAnswersResult,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            loading: executingUpdateMultipleAnswers,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            error: updateErrorMultipleAnswers
        }
    ] = useMutation<UpdateQuestionMultipleAnswers, UpdateQuestionMultipleAnswersVariables>(updateQuestionsMultipleAnswers, {
        refetchQueries: [getQuizQuestions]
    });

    const getDbQuestionById = (id: number): QuizQuestions_quiz_master_quizzes_by_pk_questions => {
        return quizQuestions?.quiz_master_quizzes_by_pk!.questions.find((q) => q.id === id)!;
    };

    const onSaveQuestion = (question: Question) => {
        if (!question.id) {
            // insert question
            if (question.answers.length === 1) {
                question.answers[0].isCorrect = true;
            }
            onBulkInsert([question]);
            setQuestionToEdit(null);
            return;
        }

        // update question
        const dbQuestion = getDbQuestionById(question.id);
        if (
            question.answers.length === 1 &&
            dbQuestion.answers.length === 1 &&
            question.answers.at(0)?.id === dbQuestion.answers.at(0)?.id
        ) {
            updateQuestion({
                variables: {
                    question_id: question.id!,
                    answer_id: question.answers[0].id!,
                    question: question.question!,
                    answer: question.answers[0].answer!,
                    question_type_id: question.questionTypeId,
                    genre_id: question.genreId ?? undefined,
                    content_url: question.contentUrl,
                    is_correct: true
                }
            });
        } else {
            const answersToDelete = differenceBy(dbQuestion.answers, question.answers, 'id');
            const answersToInsert = question.answers.filter((a) => !a.id);

            updateAllAnswers({
                variables: {
                    deletedAnswerIds: answersToDelete.map((a) => a.id),
                    newAnswers: answersToInsert.map((a) => ({
                        answer: a.answer,
                        is_correct: a.isCorrect,
                        quiz_question_id: question.id
                    }))
                }
            });
            updateQuestion({
                variables: {
                    question_id: question.id!,
                    question: question.question!,
                    question_type_id: question.questionTypeId,
                    genre_id: question.genreId ?? undefined,
                    content_url: question.contentUrl,
                    answer_id: -1,
                    answer: '',
                    is_correct: true
                }
            });
        }

        setQuestionToEdit(null);
    };

    const onBulkInsert = (questions: Question[]) => {
        const questionsWithAnswers: quiz_master_quiz_questions_insert_input[] = questions.map((q) => ({
            quiz_id: quiz.id,
            question: q.question!,
            genre_id: q.genreId ?? quiz.genreId,
            question_type_id: q.questionTypeId ?? QuestionType.Text,
            content_url: q.contentUrl,
            question_answers: {
                data: q.answers.map((a) => ({
                    answer: a.answer,
                    is_correct: a.isCorrect
                }))
            }
        }));
        bulkInsertQuestion({
            variables: { objects: questionsWithAnswers }
        });
    };

    const constructQuestionFromImportModel = (importData: QuestionImportModel): Question => {
        const question: Partial<Question> = {
            question: importData.question,
            genreId: importData.genreId ?? quiz.genreId,
            contentUrl: importData.contentUrl,
            questionTypeId: importData.questionType
                ? QuestionType[capitalizeFirstLetter<'Text' | 'Image' | 'Audio' | 'Video'>(importData.questionType.toLowerCase())]
                : QuestionType.Text
        };
        question.answers = !importData.answerOptions
            ? [
                  {
                      answer: importData.answer.toString(),
                      isCorrect: true
                  }
              ]
            : importData.answerOptions!.map((option, index) => ({
                  answer: option,
                  isCorrect: isNumber(importData.answer)
                      ? index === importData.answer
                      : (importData.answer as string).toLowerCase() === option.toLowerCase()
              }));

        return question as Question;
    };

    const onBulkInsertFromJson = (questions: QuestionImportModel[]) => {
        const questionsWithAnswers: Question[] = questions.map((q) => constructQuestionFromImportModel(q));
        onBulkInsert(questionsWithAnswers);
    };

    const handleRowDelete = (record: TableQuestionWithAnswer) => {
        deleteQuestions({ variables: { questionIds: [record.id!] } });
    };

    const handleTableRowEdit = (record: TableQuestionWithAnswer) => {
        const dbQuestion = getDbQuestionById(record.id!);
        if (!!dbQuestion) {
            setQuestionToEdit(null);
        }

        setQuestionToEdit(cloneDeep(dbQuestion));
    };

    const handleDeleteSelectedRows = (selectedIds: readonly TableQuestionWithAnswer[keyof TableQuestionWithAnswer][]) => {
        deleteQuestions({ variables: { questionIds: selectedIds as number[] } });
    };

    if (loading) return <p>Loading...</p>;
    if (error || updateError || deleteError || bulkInsertError || updateErrorMultipleAnswers)
        return (
            <div className="App">
                {'Error :('}
                <br />
                <pre>
                    {JSON.stringify(error || updateError || deleteError || bulkInsertError || updateErrorMultipleAnswers, undefined, 4)}
                </pre>
                <button type="button" onClick={() => window.location.reload()}>
                    Reload
                </button>
            </div>
        );
    if (!quizQuestions) return <p>No data</p>;

    const questions: TableQuestionWithAnswer[] = quizQuestions.quiz_master_quizzes_by_pk!.questions.map((q) => {
        return {
            ...omit(q, 'answers', 'questionTypeId', 'genreId'),
            questionType: QuestionType[q.questionTypeId],
            genre: q.genreId ? globalContext.getGenreName(q.genreId) : null,
            answer: q.answers.length > 1 ? 'Multiple Choice' : q.answers[0]!.answer,
            isCorrect: q.answers.length > 1 ? true : q.answers[0]!.isCorrect
        };
    });

    const headCells: TableHeadCell<TableQuestionWithAnswer>[] = [
        // {
        //     id: 'id',
        //     disablePadding: true,
        //     label: 'ID'
        // },
        {
            id: 'question',
            disablePadding: true,
            label: 'Question'
        },
        {
            id: 'questionType',
            disablePadding: true,
            label: 'Question Type'
        },
        {
            id: 'genre',
            disablePadding: true,
            label: 'Genre'
        },
        {
            id: 'contentUrl',
            disablePadding: true,
            label: 'Content URL'
        },
        {
            id: 'answer',
            disablePadding: true,
            label: 'Answer'
        }
    ];

    return (
        <div className="edit-quiz-questions-wrapper">
            <ConfirmDialog title="Confirm" message="Are you sure you want to delete these questions?">
                {(confirm: (callbackFunc: Function) => (event: any) => void) => (
                    <Box sx={{ width: '80%', alignContent: 'center' }}>
                        <Paper sx={{ width: '100%', mb: 2 }}>
                            <EnhancedTable<TableQuestionWithAnswer>
                                title="Questions"
                                data={questions}
                                headerCells={headCells}
                                dataRecordIdKey="id"
                                showTableCheckboxes={true}
                                onRowDeleteClick={confirm(handleRowDelete)}
                                onRowEditClick={handleTableRowEdit}
                                onDeleteSelectedClick={confirm(handleDeleteSelectedRows)}
                            />
                        </Paper>
                    </Box>
                )}
            </ConfirmDialog>
            <br />
            <QuestionDetail question={editedQuestion} onSaveQuestion={onSaveQuestion} />
            <br />
            <Accordion sx={{ width: '80%' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="advanced-accordion-content" id="advanced-accordion-header">
                    <Typography>Advanced</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="body2">Import JSON file with an array of question objects.&ensp;
                    <Tooltip placement="top"
                        title={
                            <Typography variant="body2">
                                    interface QuestionImportModel {'{'}
                                    
                                    <br/>&ensp;question: string; 
                                    <br/>&ensp;answer: number | string;
                                    <br/>&ensp;answerOptions?: string[];
                                    <br/>&ensp;contentUrl?: string;
                                    <br/>&ensp;genreId?:
                                    number;
                                    <br/>&ensp;questionType?: 'Text' | 'Image' | 'Audio' | 'Video';
                                    <br/>{'}'}
                            </Typography>
                        }
                    >
                        <DataObjectIcon fontSize="small" />
                    </Tooltip></Typography>

                    <BulkUploadJson<QuestionImportModel>
                        title="Questions with single answer to insert"
                        onProcessUploadedData={onBulkInsertFromJson}
                    />
                </AccordionDetails>
            </Accordion>
            <br />
            {(executingDelete || executingUpdate || executingUpdateMultipleAnswers || executingBulkInsert) && <Spinner />}
        </div>
    );
};
